import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FeaturedProperties } from "@/components/property";
import { HeroSection, PropertyTypes, InvestmentBanner, AboutSection } from "@/components/sections";
import { FloatingChat, Header, Footer, MobileHeader } from "@/components/layout";
import { HomeSEO } from "@/components/seo";
import { useIsMobile } from "@/hooks/use-mobile";
import { useImobiliaria } from "@/hooks/useImobiliaria";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { useImoveis } from "@/hooks/useImoveis";
import { useRegisterSiteVisit } from "@/hooks/useLeads";
import { useAvailablePropertyTypes } from "@/hooks/useAvailablePropertyTypes";
import { imoveisToPropertyCards } from "@/adapters/propertyAdapter";
import { MOCK_IMOBILIARIA, MOCK_SITE_CONFIG, MOCK_FEATURED_PROPERTIES } from "@/data/mockData";

export default function Index() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  // Buscar dados da imobiliária De Sordi Broker
  const { data: imobiliaria, isLoading: loadingImobiliaria } = useImobiliaria("desordibroker");
  const { data: siteConfig, isLoading: loadingSite } = useSiteConfig(imobiliaria?.id);
  const { data: imoveis, isLoading: loadingImoveis } = useImoveis({
    imobiliariaId: imobiliaria?.id,
    limit: 16,
    orderBy: { column: "created_at", ascending: false },
  });

  // Buscar tipos de imóveis disponíveis
  const { data: availableTypes, isLoading: loadingTypes } = useAvailablePropertyTypes(imobiliaria?.id);

  // Registrar visita ao site
  const { mutate: registerVisit } = useRegisterSiteVisit();

  useEffect(() => {
    if (siteConfig?.id) {
      registerVisit(siteConfig.id);
    }
  }, [siteConfig?.id, registerVisit]);

  // Usar fallback para dados mockados se não houver dados reais
  const imobiliariaData = imobiliaria || MOCK_IMOBILIARIA;
  const siteConfigData = siteConfig || MOCK_SITE_CONFIG;

  // Converter imóveis para formato do PropertyCard
  const featuredProperties = imoveis && imoveis.length > 0 ? imoveisToPropertyCards(imoveis) : [];

  const handleNavigateToProperties = () => {
    navigate("/search-grid");
  };

  const handleNavigateToAbout = () => {
    const aboutSection = document.querySelector("#about-section");
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleAnnounceClick = () => {
    navigate("/anuncie");
  };

  const handleContactClick = () => {
    const whatsapp = siteConfigData?.telefone_contato || "19999999999";
    const message = encodeURIComponent("Olá! Gostaria de mais informações.");
    window.open(`https://wa.me/55${whatsapp}?text=${message}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <HomeSEO
        siteName={imobiliariaData.nome}
        description={siteConfigData?.texto_principal || undefined}
        city={siteConfigData?.titulo_principal?.split(" ").pop() || "Campinas"}
      />

      <div className="w-full mx-auto max-w-[1920px]">
        {isMobile ? (
          <MobileHeader
            onNavigateToProperties={handleNavigateToProperties}
            onNavigateToAbout={handleNavigateToAbout}
            onAnnounceClick={handleAnnounceClick}
          />
        ) : (
          <Header
            onNavigateToProperties={handleNavigateToProperties}
            onNavigateToAbout={handleNavigateToAbout}
            onAnnounceClick={handleAnnounceClick}
          />
        )}

        <HeroSection
          showSearch={true}
          searchProps={{
            variant: "hero",
            showTabs: true,
            onSearch: (searchFilters) => {
              // Converter filtros do SearchBar para formato ImoveisFilters
              const filters: any = {};

              // Processar tipo de anúncio
              if (searchFilters.type === "alugar") {
                filters.finalidade = "locacao";
              } else if (searchFilters.type === "vender") {
                filters.finalidade = "venda";
              }

              // Processar busca textual (cidade + bairro)
              const searchTerms = [];
              if (searchFilters.cidade?.trim()) {
                searchTerms.push(searchFilters.cidade.trim());
              }
              if (searchFilters.bairro?.trim()) {
                searchTerms.push(searchFilters.bairro.trim());
              }
              if (searchTerms.length > 0) {
                filters.search = searchTerms.join(" ");
              }

              // Processar valor máximo
              if (searchFilters.valorMax) {
                const valorNumerico = parseFloat(searchFilters.valorMax.replace(/[^\d]/g, ""));
                if (!isNaN(valorNumerico) && valorNumerico > 0) {
                  filters.valor_max = valorNumerico;
                }
              }

              // Processar quartos mínimos
              if (searchFilters.quartos && searchFilters.quartos !== "0") {
                const quartosNum = parseInt(searchFilters.quartos);
                if (!isNaN(quartosNum) && quartosNum > 0) {
                  filters.quartos_min = quartosNum;
                }
              }

              navigate("/search-grid", { state: { filters } });
            },
          }}
        />

        <div className="px-4 md:px-6 lg:px-10">
          <FeaturedProperties
            properties={featuredProperties}
            showFilters={true}
            dots={false}
            showViewAll={true}
            filterOptions={[
              { id: "todos", label: "Todos" },
              { id: "alugar", label: "Alugar" },
              { id: "comprar", label: "Comprar" },
            ]}
            onViewAll={() => navigate("/search-grid")}
            onPropertyClick={(propertyId) => {
              const property = featuredProperties.find((p) => p.id === propertyId);
              if (property?.code) {
                navigate(`/imovel/${property.code}`);
              }
            }}
            variant="carousel"
            maxVisible={isMobile ? 1 : typeof window !== "undefined" && window.innerWidth < 1024 ? 2 : 4}
            className="mt-6 md:mt-8 lg:mt-20"
          />
        </div>

        {/* Investment Banner Section */}
        {imobiliariaData?.subdominio === "desordibroker" ? (
          <div className="px-4 md:px-6 lg:px-10 mt-6 md:mt-8 lg:mt-12">
            <InvestmentBanner />
          </div>
        ) : (
          <></>
        )}

        <div className="px-4 md:px-6 lg:px-10 mt-6 md:mt-8 lg:mt-12">
          <PropertyTypes
            availableTypes={availableTypes}
            isLoading={loadingTypes}
            onPropertyTypeClick={(type) => {
              // Usar tipoDb diretamente (já está no formato correto)
              if (type.tipoDb) {
                navigate("/search-grid", {
                  state: {
                    filters: { tipo: [type.tipoDb] }, // Passar como array
                  },
                });
              }
            }}
          />
        </div>

        {imobiliariaData?.subdominio === "desordibroker" ? (
          <div id="about-section" className="px-4 md:px-6 lg:px-10 mt-6 md:mt-8 lg:mt-12">
            <AboutSection />
          </div>
        ) : (
          <></>
        )}

        <Footer onContactClick={handleContactClick} />
      </div>

      {/* Floating Chat Button */}
      <FloatingChat position="bottom-right" size="md" onClick={handleContactClick} />
    </div>
  );
}
